import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadAPI, userAPI } from '../lib/api';
import { formatDate, getStatusColor, getClientTypeLabel } from '../lib/utils';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Phone,
  Mail,
  Building2,
  ArrowRight,
  MessageSquare,
  XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const LEAD_STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

const CLIENT_TYPES = [
  { value: 'hospital', label: 'Hospital' },
  { value: 'lab', label: 'Laboratory' },
  { value: 'diagnostic', label: 'Diagnostic Center' },
];

export const LeadsPage = () => {
  const navigate = useNavigate();
  usePageTitle('Leads');
  const [leads, setLeads] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isCloseOpen, setIsCloseOpen] = useState(false);
  const [closeForm, setCloseForm] = useState({ reason: '', status: 'lost' });

  const [formData, setFormData] = useState({
    organization_name: '',
    contact_person: '',
    email: '',
    phone: '',
    client_type: 'hospital',
    source: '',
    notes: '',
  });

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const [leadsRes, staffRes] = await Promise.all([
        leadAPI.getAll(params),
        userAPI.getAll({ role: 'staff' }).catch(() => ({ data: [] })),
      ]);
      
      setLeads(leadsRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await leadAPI.create(formData);
      toast.success('Lead created successfully!');
      setIsCreateOpen(false);
      setFormData({
        organization_name: '',
        contact_person: '',
        email: '',
        phone: '',
        client_type: 'hospital',
        source: '',
        notes: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await leadAPI.update(leadId, { status: newStatus });
      toast.success('Status updated!');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAssign = async (leadId, staffId) => {
    try {
      await leadAPI.update(leadId, { assigned_to: staffId });
      toast.success('Lead assigned!');
      loadData();
    } catch (error) {
      toast.error('Failed to assign lead');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedLead) return;
    try {
      await leadAPI.addCommunication(selectedLead.id, {
        message: newNote,
        type: 'note',
      });
      toast.success('Note added!');
      setNewNote('');
      // Refresh lead details
      const res = await leadAPI.getOne(selectedLead.id);
      setSelectedLead(res.data);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleConvert = (lead) => {
    navigate('/clients', { state: { lead } });
  };

  const handleCloseLead = async () => {
    if (!closeForm.reason.trim()) { toast.error('Please enter a reason'); return; }
    try {
      await leadAPI.close(selectedLead.id, closeForm);
      toast.success(`Lead "${selectedLead.organization_name}" closed`);
      setIsCloseOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close lead');
    }
  };

  const filteredLeads = leads.filter(
    (lead) =>
      lead.organization_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStaffName = (staffId) => {
    const staffMember = staff.find((s) => s.id === staffId);
    return staffMember?.name || 'Unassigned';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="leads-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-orange-600 via-amber-500 to-yellow-400 p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">Lead Management</h1>
            <p className="text-orange-100">Track and manage your sales pipeline</p>
          </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-lead-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Lead</DialogTitle>
              <DialogDescription>Enter the lead details below</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="organization_name">Organization Name *</Label>
                  <Input
                    id="organization_name"
                    value={formData.organization_name}
                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                    required
                    data-testid="lead-org-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_person">Contact Person *</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    required
                    data-testid="lead-contact-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_type">Type *</Label>
                  <Select
                    value={formData.client_type}
                    onValueChange={(value) => setFormData({ ...formData, client_type: value })}
                  >
                    <SelectTrigger data-testid="lead-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="lead-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    data-testid="lead-phone-input"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="source">Lead Source</Label>
                  <Input
                    id="source"
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    placeholder="Website, Referral, etc."
                    data-testid="lead-source-input"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    data-testid="lead-notes-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-lead-btn">Create Lead</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-leads-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="status-filter-select">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {LEAD_STATUSES.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Leads Grid */}
      {filteredLeads.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">No leads found. Add your first lead to get started!</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLeads.map((lead) => (
            <Card key={lead.id} className="hover:shadow-md transition-shadow" data-testid={`lead-card-${lead.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Building2 className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{lead.organization_name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{lead.contact_person}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`lead-menu-${lead.id}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setSelectedLead(lead); setIsDetailOpen(true); }}>
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleStatusChange(lead.id, 'contacted')}>
                        Mark Contacted
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleStatusChange(lead.id, 'proposal_sent')}>
                        Send Proposal
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {lead.status !== 'won' && lead.status !== 'lost' && (
                        <>
                          <DropdownMenuItem onClick={() => handleConvert(lead)} className="text-green-600">
                            <ArrowRight className="w-4 h-4 mr-2" />
                            Convert to Client
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelectedLead(lead); setCloseForm({ reason: '', status: 'lost' }); setIsCloseOpen(true); }} className="text-red-600" data-testid={`close-lead-${lead.id}`}>
                            <XCircle className="w-4 h-4 mr-2" />
                            Close Lead
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge className={getStatusColor(lead.status)}>
                    {lead.status?.replace('_', ' ')}
                  </Badge>
                  <Badge variant="outline">{getClientTypeLabel(lead.client_type)}</Badge>
                </div>
                {lead.close_reason && (
                  <div className="text-sm bg-red-50 text-red-700 p-2 rounded border border-red-200">
                    <span className="font-medium">Close Reason:</span> {lead.close_reason}
                  </div>
                )}
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="w-3.5 h-3.5" />
                    <span className="truncate">{lead.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="w-3.5 h-3.5" />
                    <span>{lead.phone}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-xs text-muted-foreground">
                    {lead.source || 'No source'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(lead.created_at)}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Lead Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedLead?.organization_name}</DialogTitle>
            <DialogDescription>Lead details and communication history</DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-6">
              {/* Lead Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Contact Person</p>
                  <p className="font-medium">{selectedLead.contact_person}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium">{getClientTypeLabel(selectedLead.client_type)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedLead.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedLead.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={getStatusColor(selectedLead.status)}>
                    {selectedLead.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Assigned To</p>
                  <Select
                    value={selectedLead.assigned_to || ''}
                    onValueChange={(value) => handleAssign(selectedLead.id, value)}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue placeholder="Assign staff" />
                    </SelectTrigger>
                    <SelectContent>
                      {staff.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Notes Section */}
              {selectedLead.notes && (
                <div>
                  <h4 className="font-medium mb-2">Notes</h4>
                  <p className="text-sm text-muted-foreground">{selectedLead.notes}</p>
                </div>
              )}

              {/* Communication History */}
              <div>
                <h4 className="font-medium mb-3">Communication History</h4>
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {selectedLead.communication_history?.length > 0 ? (
                    selectedLead.communication_history.map((comm) => (
                      <div key={comm.id} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{comm.created_by_name}</span>
                          <span className="text-xs text-muted-foreground">{formatDate(comm.created_at)}</span>
                        </div>
                        <p className="text-sm">{comm.message}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No communication history yet</p>
                  )}
                </div>
              </div>

              {/* Add Note */}
              <div className="flex gap-2">
                <Textarea
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={2}
                />
                <Button onClick={handleAddNote} disabled={!newNote.trim()}>
                  <MessageSquare className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Close Lead Dialog */}
      <Dialog open={isCloseOpen} onOpenChange={setIsCloseOpen}>
        <DialogContent className="max-w-md" data-testid="close-lead-dialog">
          <DialogHeader>
            <DialogTitle>Close Lead</DialogTitle>
            <DialogDescription>
              Close "{selectedLead?.organization_name}" with a reason
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Status</Label>
              <Select value={closeForm.status} onValueChange={v => setCloseForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="close-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lost">Lost</SelectItem>
                  <SelectItem value="won">Won (No Conversion)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason *</Label>
              <Textarea
                value={closeForm.reason}
                onChange={e => setCloseForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="e.g., Budget constraints, Chose competitor, Not interested..."
                rows={3}
                data-testid="close-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCloseOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleCloseLead} data-testid="confirm-close-lead-btn">
              <XCircle className="w-4 h-4 mr-2" /> Close Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadsPage;
